import './header.scss';

import { HeaderProps, useHeader, useMobileHeaderDrawer } from '@components/header/headerHooks';
import HelixImage from '@components/helixImage';
import HelixLink from '@components/link';
import RenderIf from '@components/utility/renderIf';
import { Close as CloseIcon, Menu as MenuIcon } from '@mui/icons-material';
import { alpha, AppBar, Box, Drawer, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { cloneElement, FC } from 'react';

const Header: FC<HeaderProps> = (props) => {
  const { title, logo, centerLink, desktopRightSideElements, mobileDrawerElements } = props;

  useHeader(props);

  return (
    <ScalableAppBar position="static">
      <nav className="header-nav" aria-label="Primary">
        <div className="left-nav-section">
          <HelixLink href={logo.href}>
            <HelixImage
              imageBreakpoints={{ md: logo.desktopImageSrc, xs: logo.mobileImageSrc }}
              display="flex"
              height="32px"
              altText={logo.altText}
            />
          </HelixLink>
        </div>
        <Box className="header-centerNavContent" sx={{ flex: { xs: 2, md: 6 } }}>
          <RenderIf condition={!!centerLink}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <a className="header-centerNavContentAdminLink" href={centerLink?.href}>
                {centerLink?.label}
              </a>
            </Box>
          </RenderIf>
          <span>{title}</span>
        </Box>
        <div className="right-nav-section">
          <RenderIf condition={!!mobileDrawerElements}>
            <MobileHeaderLinks mobileDrawerElements={mobileDrawerElements} />
          </RenderIf>
          <RenderIf condition={!!desktopRightSideElements}>
            <DesktopHeaderLinks desktopRightSideElements={desktopRightSideElements} />
          </RenderIf>
        </div>
      </nav>
    </ScalableAppBar>
  );
};

const DesktopHeaderLinks: FC<Pick<HeaderProps, 'desktopRightSideElements'>> = ({ desktopRightSideElements }) => {
  return (
    <Box component="ul" sx={{ display: { xs: 'none', md: 'flex' } }}>
      {desktopRightSideElements?.map((element) => {
        if (!element.key) {
          throw new Error('React prop "key" required for all desktop header links');
        }
        return <li key={element.key}>{element}</li>;
      })}
    </Box>
  );
};

const MobileHeaderLinks: FC<Pick<HeaderProps, 'mobileDrawerElements'>> = ({ mobileDrawerElements }) => {
  const { isDrawerOpen, handleDrawerOpen, handleDrawerClose } = useMobileHeaderDrawer();

  return (
    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
      <div className="mobileHeader-menuButtonContainer">
        <MobileMenuIconButton
          aria-label="Open Navigation Drawer"
          aria-haspopup="true"
          aria-controls="mobileHeader-drawer"
          aria-expanded={isDrawerOpen}
          onClick={handleDrawerOpen}
          edge="end"
          size="large"
        >
          <MenuIcon />
        </MobileMenuIconButton>
      </div>
      <Drawer anchor="top" variant="persistent" open={isDrawerOpen}>
        <div className="mobileHeader-drawerHeader">
          <div className="mobileHeader-drawerHeaderTextContainer">
            <span className="mobileHeader-headerText">My Embark</span>
          </div>
          <MobileMenuIconButton onClick={handleDrawerClose} aria-label="Close Navigation Drawer" size="large">
            <CloseIcon />
          </MobileMenuIconButton>
        </div>
        <ul className="mobileHeader-links" role="menu">
          {mobileDrawerElements?.map((element) => {
            if (!element.key) {
              throw new Error('React prop "key" required for all mobile header links');
            }
            const elementWithRole = cloneElement(element, { role: 'menuitem' });
            return <li key={element.key}>{elementWithRole}</li>;
          })}
        </ul>
      </Drawer>
    </Box>
  );
};

const MobileMenuIconButton = styled(IconButton)(({ theme }) => {
  const smallButtonSize = 4.5;
  const largeButtonSize = 6;
  return {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: alpha(theme.palette.black.main, 0.04),
    },
    '&:focus': {
      backgroundColor: alpha(theme.palette.black.main, 0.04),
    },
    '&.MuiIconButton-sizeSmall': {
      width: theme.spacing(smallButtonSize),
      height: theme.spacing(smallButtonSize),
    },
    '&.MuiIconButton-sizeSmall .MuiSvgIcon-root': {
      width: theme.spacing(smallButtonSize / 2),
      height: theme.spacing(smallButtonSize / 2),
    },
    '&.MuiIconButton-sizeLarge': {
      width: theme.spacing(largeButtonSize),
      height: theme.spacing(largeButtonSize),
    },
    '&.MuiIconButton-sizeLarge .MuiSvgIcon-root': {
      width: theme.spacing(largeButtonSize / 2),
      height: theme.spacing(largeButtonSize / 2),
    },
  };
});

const ScalableAppBar = styled(AppBar)(({ theme }) => ({
  height: '64px',
  [theme.breakpoints.down('lg')]: {
    height: '56px',
  },
}));

export { Header as default, Header };
export type { HeaderProps };
